import { z } from 'zod';
import { extractedDesignFactsAiSchema } from '../models/extracted-design-facts.model';

/** OpenAI structured output name for batch merge. */
export const PROJECT_FILE_BATCH_MERGE_STRUCTURED_OUTPUT_NAME =
  'project_file_batch_consolidation' as const;

/** Composition relationship between two or more `aggregated_buildings`. */
export const PROJECT_FILE_BATCH_MERGE_COMPOSITION_RELATIONSHIPS = [
  'shared_base_slab',
  'connected_volume',
  'separate',
  'unknown',
] as const;
export type ProjectFileBatchMergeCompositionRelationship =
  (typeof PROJECT_FILE_BATCH_MERGE_COMPOSITION_RELATIONSHIPS)[number];

const nullableArea = (description: string): Record<string, unknown> => ({
  type: ['number', 'null'],
  description,
});

/** Printed area totals for a body that is not one `design_facts` building. */
const areaTotalsJsonSchema = (
  description: string,
): Record<string, unknown> => ({
  type: 'object',
  additionalProperties: false,
  description,
  properties: {
    bta_m2: nullableArea('BTA in m²; null when no source states it.'),
    ljus_bta_m2: nullableArea('Ljus BTA in m²; null when no source states it.'),
    boa_m2: nullableArea('BOA in m²; null when no source states it.'),
    loa_m2: nullableArea('LOA in m²; null when no source states it.'),
    bya_m2: nullableArea('BYA in m²; null when no source states it.'),
  },
  required: ['bta_m2', 'ljus_bta_m2', 'boa_m2', 'loa_m2', 'bya_m2'],
});

const nullableAreaTotalsJsonSchema = (
  description: string,
): Record<string, unknown> => ({
  ...areaTotalsJsonSchema(description),
  type: ['object', 'null'],
});

const toOpenAiStrictJsonSchema = (
  schema: z.ZodType,
): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(schema) as Record<
    string,
    unknown
  > & { $schema?: string };
  return rest;
};

/**
 * Root strict JSON Schema for batch consolidation (`project_file_batch_consolidation`).
 */
export const buildProjectFileBatchMergeStructuredOutputRootJsonSchema =
  (): Record<string, unknown> => ({
    type: 'object',
    additionalProperties: false,
    properties: {
      suggested_project_name: {
        type: ['string', 'null'],
        description:
          'Single best project/job title for the batch; Swedish or English; null if no grounded merge.',
      },
      suggested_project_description: {
        type: ['string', 'null'],
        description:
          'Merged short description of scope/site/building; null if not mergeable.',
      },
      aggregated_buildings: {
        type: 'array',
        description:
          'One row per distinct building as labelled in the source documents. Each description is plain prose for humans and downstream UX — never list tool/control names.',
        items: {
          type: 'object',
          additionalProperties: false,
          properties: {
            building_instance_key: {
              type: ['string', 'null'],
              description:
                'Stable id aligned with extractor `building_instance_key` when known; null otherwise.',
            },
            building_label: {
              type: ['string', 'null'],
              description: 'Stamp or table name; null if unknown.',
            },
            description_for_design_ai: {
              type: 'string',
              description:
                'Human-readable prose only: geometry in ordinary words (outline, bar, courtyard, storey count, approximate areas, notable dimensions, façade notes, circulation, location if known). No tool/API identifiers, snake_case control names, or software jargon.',
            },
          },
          required: [
            'building_instance_key',
            'building_label',
            'description_for_design_ai',
          ],
        },
      },
      building_compositions: {
        type: 'array',
        description:
          'Cross-building composition rows. Emit ONE row whenever two or more `aggregated_buildings` form a single physical composition in reality (e.g. two residential blocks sitting on a shared garage podium / källare / bottenplatta, or buildings joined by a connector volume). Empty array when every building stands fully separate. Use this layer to flag cases where the document labels several "hus" but they should be modelled as one nodon building.',
        items: {
          type: 'object',
          additionalProperties: false,
          properties: {
            composing_building_keys: {
              type: 'array',
              description:
                'Two or more `aggregated_buildings[].building_instance_key` values that participate in this composition. Use the same key strings as in `aggregated_buildings`.',
              items: { type: 'string' },
            },
            relationship: {
              type: 'string',
              enum: [...PROJECT_FILE_BATCH_MERGE_COMPOSITION_RELATIONSHIPS],
              description:
                '`shared_base_slab` for buildings sitting on a continuous structural base (garage podium, gemensam källare, delad bottenplatta). `connected_volume` for buildings joined by a non-trivial connector mass. `separate` for adjacent but independent buildings (do not emit a row for these unless explicitly clarifying). `unknown` when documents are ambiguous.',
            },
            composition_note: {
              type: 'string',
              description:
                'One short prose sentence in the document language describing the composition — quote terms like "gemensamt garage", "podium", "delad bottenplatta", "sammanbyggda" when present. Grounded in the extraction prose; do not invent.',
            },
            should_model_as_single_building: {
              type: 'boolean',
              description:
                'True when the composition is structurally one building for downstream design-AI modelling (typically `shared_base_slab` and significant `connected_volume`). False for `separate` / minor connectors / `unknown`.',
            },
            areas: nullableAreaTotalsJsonSchema(
              'Totals for the composed body; null when `should_model_as_single_building` is false (a total for those buildings then goes on `project_areas`). A candidate whose `covers_building_keys` are exactly these buildings gives its printed value. Otherwise, when every composing building has a printed total for that area, their sum. Otherwise null.',
            ),
          },
          required: [
            'composing_building_keys',
            'relationship',
            'composition_note',
            'should_model_as_single_building',
            'areas',
          ],
        },
      },
      project_areas: areaTotalsJsonSchema(
        'Totals printed for several buildings that are not one building and not one composition modelled as a single building (a project-wide areatabell total, or a pair that stays separate). Never a value of one building. Null leaves when no such total is printed.',
      ),
      design_facts: {
        ...toOpenAiStrictJsonSchema(extractedDesignFactsAiSchema),
        description:
          'Resolved design facts: one value per leaf, or null. One `buildings[]` row per physical building — collapse classifier alias keys. Pick from `design_fact_candidates` using the ranking in the instructions. Never average. Never emit candidate arrays.',
      },
    },
    required: [
      'suggested_project_name',
      'suggested_project_description',
      'aggregated_buildings',
      'building_compositions',
      'project_areas',
      'design_facts',
    ],
  });
